import React, { useState, useCallback, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Search,
  Upload,
  Copy,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Loader2,
  FileText,
  Database,
  TrendingUp,
  Globe,
  Newspaper,
  User,
  Info,
  CalendarIcon,
  Phone,
  Building2
} from 'lucide-react';
import { format } from 'date-fns';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import DashboardLayout from '../components/DashboardLayout';
import { SearchSection } from '../components/SearchSection';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOURCE_TYPE_CONFIG = {
  PORTAL: {
    label: 'Portal',
    icon: Globe,
    helperText: 'Paste one URL per line (max 10,000). Format: https://example.com/property/123',
    placeholder: `https://99acres.com/property/123456\nhttps://magicbricks.com/listing/789\nhttps://housing.com/buy/12345`,
    color: '#3b82f6',
    isMetadata: false
  },
  NEWSPAPER: {
    label: 'Newspaper',
    icon: Newspaper,
    helperText: 'Select source name and date, then paste mobile numbers. Duplicates allowed. (max 10,000)',
    placeholder: `9876543210\n8765432109\n7654321098\n+91 9988776655\n91-8877665544`,
    color: '#8b5cf6',
    isMetadata: true
  },
  OWNER_REFERENCE: {
    label: 'Owner Reference',
    icon: User,
    helperText: 'Select source name and date, then paste mobile numbers (one per line, max 10,000)',
    placeholder: `9876543210\n8765432109\n7654321098\n+91 9988776655`,
    color: '#14b8a6',
    isMetadata: true
  },
  CUSTOM_PROVIDER: {
    label: 'Custom Provider',
    icon: Building2,
    helperText: 'Select provider name and date, then paste mobile numbers (one per line, max 10,000)',
    placeholder: `9876543210\n8765432109\n7654321098\n+91 9988776655`,
    color: '#f59e0b',
    isMetadata: true
  }
};

const PIE_COLORS = {
  PORTAL: '#3b82f6',
  NEWSPAPER: '#8b5cf6',
  OWNER_REFERENCE: '#14b8a6',
  CUSTOM_PROVIDER: '#f59e0b',
  OTHER: '#6b7280',
  MANUAL: '#ec4899'
};

// Custom tooltip component for the pie chart
const CustomTooltip = ({ active, payload, stats }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const percentage = stats?.total > 0 ? ((data.value / stats.total) * 100).toFixed(1) : 0;
    return (
      <div className="bg-white border border-border rounded-lg shadow-lg p-3">
        <p className="font-medium">{data.name}</p>
        <p className="text-sm text-muted-foreground">{data.value.toLocaleString()} records</p>
        <p className="text-sm font-medium" style={{ color: data.color }}>{percentage}%</p>
      </div>
    );
  }
  return null;
};

const DashboardPage = () => {
  const { token, isMainUser } = useAuth();
  const [sourceType, setSourceType] = useState('PORTAL');
  const [bulkInput, setBulkInput] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('submit');
  const [submissionSummary, setSubmissionSummary] = useState(null);
  
  // Metadata fields
  const [sourceMasters, setSourceMasters] = useState([]);
  const [selectedSourceMaster, setSelectedSourceMaster] = useState('');
  const [sourceDate, setSourceDate] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const axiosConfig = {
    headers: { Authorization: `Bearer ${token}` }
  };

  const refreshStats = async () => {
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const statsRes = await axios.get(`${API_URL}/api/source/stats`, config);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to refresh stats:', error);
    }
  };

  // Fetch stats and source masters on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const [statsRes, mastersRes] = await Promise.all([
          axios.get(`${API_URL}/api/source/stats`, config),
          axios.get(`${API_URL}/api/source-master`, config)
        ]);
        setStats(statsRes.data);
        setSourceMasters(mastersRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    if (token) {
      fetchData();
    }
  }, [token]);

  // Filter source masters by type
  const filteredSourceMasters = sourceMasters.filter(
    sm => sm.source_type === sourceType && sm.status === 'ACTIVE'
  );

  // Reset metadata when source type changes
  useEffect(() => {
    setSelectedSourceMaster('');
    setSourceDate(null);
  }, [sourceType]);

  // Prepare pie chart data
  const pieChartData = stats?.by_type 
    ? Object.entries(stats.by_type).map(([name, value]) => ({
        name,
        value,
        color: PIE_COLORS[name] || PIE_COLORS.OTHER
      }))
    : [];

  const handleBulkSubmit = async () => {
    const lines = bulkInput.trim().split('\n').filter(line => line.trim());
    
    if (lines.length === 0) {
      toast.error('Please enter at least one value');
      return;
    }

    if (lines.length > 10000) {
      toast.error('Maximum 10,000 entries allowed per submission');
      return;
    }

    const currentConfig = SOURCE_TYPE_CONFIG[sourceType];
    
    // Validate metadata fields for non-PORTAL types
    if (currentConfig.isMetadata) {
      if (!selectedSourceMaster) {
        toast.error('Please select a source name');
        return;
      }
      if (!sourceDate) {
        toast.error('Please select a source date');
        return;
      }
    }

    setLoading(true);
    setProgress(0);
    setResults([]);
    setSubmissionSummary(null);

    const items = lines.map((value, index) => ({
      seq: index + 1,
      value: value.trim()
    }));

    const progressInterval = setInterval(() => {
      setProgress(prev => Math.min(prev + 2, 90));
    }, 100);

    try {
      let response;
      
      if (sourceType === 'PORTAL') {
        response = await axios.post(
          `${API_URL}/api/source/bulk-submit/portal`,
          { source_type: 'PORTAL', items },
          axiosConfig
        );
      } else {
        const formattedDate = format(sourceDate, 'dd-MM-yyyy');
        response = await axios.post(
          `${API_URL}/api/source/bulk-submit/metadata`,
          { 
            source_type: sourceType,
            source_master_id: selectedSourceMaster,
            source_date: formattedDate,
            items 
          },
          axiosConfig
        );
      }

      clearInterval(progressInterval);
      setProgress(100);

      setResults(response.data.results);
      setSubmissionSummary({
        total_received: response.data.total_received,
        processed: response.data.processed,
        stored: response.data.stored,
        duplicates: response.data.duplicates,
        invalid: response.data.invalid
      });

      if (response.data.invalid > 0) {
        toast.warning(`Processed ${response.data.processed} records: ${response.data.stored} stored, ${response.data.duplicates} duplicates, ${response.data.invalid} invalid`);
      } else {
        toast.success(`Processed ${response.data.processed} records: ${response.data.stored} stored, ${response.data.duplicates} duplicates`);
      }

      // Refresh stats
      const statsResponse = await axios.get(`${API_URL}/api/source/stats`, axiosConfig);
      setStats(statsResponse.data);
    } catch (error) {
      clearInterval(progressInterval);
      toast.error(error.response?.data?.detail || 'Submission failed');
    } finally {
      setLoading(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  const copyResults = useCallback((filterType = 'all') => {
    let dataToCopy = results;
    
    if (filterType === 'duplicates') {
      dataToCopy = results.filter(r => r.status === 'DUPLICATE');
    } else if (filterType === 'invalid') {
      dataToCopy = results.filter(r => r.status === 'INVALID');
    } else if (filterType === 'stored') {
      dataToCopy = results.filter(r => r.status === 'UNIQUE');
    }

    const text = dataToCopy.map(r => 
      `${r.seq}\t${r.input}\t${r.source_uid || '-'}\t${r.status}${r.reason ? `\t${r.reason}` : ''}\t${r.submitted_at}`
    ).join('\n');

    navigator.clipboard.writeText(text);
    toast.success(`Copied ${dataToCopy.length} records`);
  }, [results]);

  const getStatusBadge = (status, reason) => {
    switch (status) {
      case 'UNIQUE':
        return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200"><CheckCircle className="h-3 w-3 mr-1" />Stored</Badge>;
      case 'DUPLICATE':
        return (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200" title={reason}>
            <AlertTriangle className="h-3 w-3 mr-1" />Duplicate
          </Badge>
        );
      case 'INVALID':
        return (
          <Badge className="bg-red-100 text-red-800 border-red-200" title={reason}>
            <XCircle className="h-3 w-3 mr-1" />Invalid
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getSourceTypeBadge = (type) => {
    const colors = {
      PORTAL: 'bg-blue-100 text-blue-800 border-blue-200',
      NEWSPAPER: 'bg-purple-100 text-purple-800 border-purple-200',
      OWNER_REFERENCE: 'bg-teal-100 text-teal-800 border-teal-200',
      CUSTOM_PROVIDER: 'bg-amber-100 text-amber-800 border-amber-200'
    };
    return <Badge className={colors[type] || 'bg-slate-100 text-slate-800'}>{type}</Badge>;
  };

  const currentConfig = SOURCE_TYPE_CONFIG[sourceType];
  const SourceIcon = currentConfig.icon;

  const lineCount = bulkInput.trim() ? bulkInput.trim().split('\n').filter(l => l.trim()).length : 0;

  // Check if form is ready to submit
  const isFormValid = () => {
    if (lineCount === 0 || lineCount > 10000) return false;
    if (currentConfig.isMetadata) {
      return selectedSourceMaster && sourceDate;
    }
    return true;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Stats Cards - Only visible to MAIN_USER */}
        {isMainUser && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Total Records Card */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
            <Card className="border-border h-full">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Active Records</CardTitle>
                <Database className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold font-heading">{stats?.total?.toLocaleString() || 0}</div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Source Type Breakdown - Text Based */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <Card className="border-border h-full">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">By Type</CardTitle>
                <FileText className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {pieChartData.slice(0, 4).map((item) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full" style={{ backgroundColor: item.color }}></div>
                        <span className="truncate">{item.name}</span>
                      </div>
                      <span className="font-medium">{item.value.toLocaleString()}</span>
                    </div>
                  ))}
                  {pieChartData.length > 4 && (
                    <div className="text-xs text-muted-foreground">+{pieChartData.length - 4} more</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Last Submission Card */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Card className="border-border h-full">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Last Submission</CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                {submissionSummary ? (
                  <div className="space-y-1">
                    <div className="text-lg font-bold text-emerald-600">{submissionSummary.stored} stored</div>
                    <div className="flex gap-3 text-sm">
                      <span className="text-amber-600">{submissionSummary.duplicates} dup</span>
                      <span className="text-red-600">{submissionSummary.invalid} invalid</span>
                    </div>
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground">No submission yet</span>
                )}
              </CardContent>
            </Card>
          </motion.div>

          {/* Pie Chart Card - Larger */}
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="md:row-span-1">
            <Card className="border-border h-full">
              <CardHeader className="flex flex-row items-center justify-between pb-0">
                <CardTitle className="text-sm font-medium text-muted-foreground">Distribution</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {pieChartData.length > 0 ? (
                  <div className="h-[120px] w-full">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          innerRadius={30}
                          outerRadius={50}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip stats={stats} />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-[120px] flex items-center justify-center text-sm text-muted-foreground">
                    No data
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </div>
        )}

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-border pb-2">
          <Button
            variant={activeTab === 'submit' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('submit')}
            data-testid="tab-submit"
            className="gap-2"
          >
            <Upload className="h-4 w-4" />
            Bulk Submit
          </Button>
          <Button
            variant={activeTab === 'search' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('search')}
            data-testid="tab-search"
            className="gap-2"
          >
            <Search className="h-4 w-4" />
            Search
          </Button>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === 'submit' && (
            <motion.div
              key="submit"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
              className="space-y-4"
            >
              {/* Bulk Input */}
              <Card className="border-border">
                <CardHeader>
                  <CardTitle className="text-lg font-heading flex items-center gap-2">
                    <Upload className="h-5 w-5" />
                    Bulk Submission
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Source Type Selector */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium flex items-center gap-2">
                      Source Type <span className="text-destructive">*</span>
                    </label>
                    <Select value={sourceType} onValueChange={setSourceType} data-testid="source-type-select">
                      <SelectTrigger className="w-full sm:w-[280px]" data-testid="source-type-trigger">
                        <SelectValue placeholder="Select source type" />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(SOURCE_TYPE_CONFIG).map(([key, config]) => {
                          const Icon = config.icon;
                          return (
                            <SelectItem key={key} value={key} data-testid={`source-type-${key.toLowerCase()}`}>
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4" />
                                {config.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Metadata Fields for NEWSPAPER / OWNER_REFERENCE / CUSTOM_PROVIDER */}
                  {currentConfig.isMetadata && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-muted/30 rounded-lg border border-border"
                    >
                      {/* Source Name Dropdown */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium flex items-center gap-2">
                          {sourceType === 'CUSTOM_PROVIDER' ? 'Provider Name' : 'Source Name'} <span className="text-destructive">*</span>
                        </label>
                        <Select 
                          value={selectedSourceMaster} 
                          onValueChange={setSelectedSourceMaster}
                          data-testid="source-master-select"
                        >
                          <SelectTrigger data-testid="source-master-trigger">
                            <SelectValue placeholder={`Select ${sourceType === 'CUSTOM_PROVIDER' ? 'provider' : 'source'} name`} />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredSourceMasters.length === 0 ? (
                              <div className="p-2 text-sm text-muted-foreground text-center">
                                No sources available. {isMainUser && "Add sources in Source Master."}
                              </div>
                            ) : (
                              filteredSourceMasters.map((sm) => (
                                <SelectItem key={sm.id} value={sm.id} data-testid={`source-master-${sm.code}`}>
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-xs bg-muted px-1 rounded">{sm.code}</span>
                                    {sm.name}
                                  </div>
                                </SelectItem>
                              ))
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Date Picker */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium flex items-center gap-2">
                          Source Date <span className="text-destructive">*</span>
                        </label>
                        <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={`w-full justify-start text-left font-normal ${!sourceDate && 'text-muted-foreground'}`}
                              data-testid="date-picker-trigger"
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {sourceDate ? format(sourceDate, 'dd-MM-yyyy') : 'Select date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={sourceDate}
                              onSelect={(date) => {
                                setSourceDate(date);
                                setCalendarOpen(false);
                              }}
                              initialFocus
                              data-testid="calendar"
                            />
                          </PopoverContent>
                        </Popover>
                      </div>
                    </motion.div>
                  )}

                  {/* Helper Text */}
                  <div className="flex items-start gap-2 p-3 bg-muted/50 rounded-lg border border-border">
                    {currentConfig.isMetadata ? (
                      <Phone className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    ) : (
                      <Info className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    )}
                    <p className="text-sm text-muted-foreground">{currentConfig.helperText}</p>
                  </div>

                  <Textarea
                    placeholder={currentConfig.placeholder}
                    value={bulkInput}
                    onChange={(e) => setBulkInput(e.target.value)}
                    className="bulk-textarea min-h-[250px] font-mono text-sm"
                    data-testid="bulk-input-textarea"
                  />

                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${lineCount > 10000 ? 'text-destructive font-medium' : 'text-muted-foreground'}`}>
                      {lineCount.toLocaleString()} / 10,000 lines
                    </span>
                    <Button
                      onClick={handleBulkSubmit}
                      disabled={loading || !isFormValid()}
                      data-testid="bulk-submit-button"
                      className="gap-2"
                    >
                      {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <SourceIcon className="h-4 w-4" />
                      )}
                      Submit {currentConfig.label}
                    </Button>
                  </div>

                  {loading && (
                    <div className="space-y-2">
                      <Progress value={progress} className="h-2" />
                      <p className="text-sm text-muted-foreground text-center">Processing... {progress}%</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Results Table */}
              {results.length > 0 && (
                <Card className="border-border">
                  <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
                    <CardTitle className="text-lg font-heading">
                      Results 
                      {submissionSummary && (
                        <span className="text-sm font-normal text-muted-foreground ml-2">
                          ({submissionSummary.stored} stored, {submissionSummary.duplicates} duplicates, {submissionSummary.invalid} invalid)
                        </span>
                      )}
                    </CardTitle>
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyResults('all')}
                        data-testid="copy-all-button"
                        className="gap-2"
                      >
                        <Copy className="h-4 w-4" />
                        Copy All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyResults('stored')}
                        data-testid="copy-stored-button"
                        className="gap-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Copy Stored
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyResults('duplicates')}
                        data-testid="copy-duplicates-button"
                        className="gap-2"
                      >
                        <AlertTriangle className="h-4 w-4" />
                        Copy Duplicates
                      </Button>
                      {submissionSummary?.invalid > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => copyResults('invalid')}
                          data-testid="copy-invalid-button"
                          className="gap-2 text-destructive border-destructive/30"
                        >
                          <XCircle className="h-4 w-4" />
                          Copy Invalid
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-hidden max-h-[500px] overflow-y-auto">
                      <Table>
                        <TableHeader className="sticky top-0 bg-background">
                          <TableRow className="bg-muted/50">
                            <TableHead className="w-16">Seq</TableHead>
                            <TableHead>Input Value</TableHead>
                            {results[0]?.source_name && <TableHead>Source</TableHead>}
                            <TableHead>Generated ID</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Reason</TableHead>
                            <TableHead>Date</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {results.map((result, index) => (
                            <TableRow 
                              key={index} 
                              className={`hover:bg-muted/30 ${
                                result.status === 'INVALID' ? 'bg-red-50/50' : 
                                result.status === 'DUPLICATE' ? 'bg-amber-50/50' : ''
                              }`}
                            >
                              <TableCell className="font-mono text-sm">{result.seq}</TableCell>
                              <TableCell className="max-w-[200px] truncate font-mono text-sm" title={result.input}>
                                {result.input}
                              </TableCell>
                              {results[0]?.source_name && (
                                <TableCell className="text-sm">{result.source_name || '-'}</TableCell>
                              )}
                              <TableCell className="font-mono text-sm text-secondary">
                                {result.source_uid || '-'}
                              </TableCell>
                              <TableCell>{getStatusBadge(result.status, result.reason)}</TableCell>
                              <TableCell className="text-sm text-muted-foreground max-w-[150px] truncate" title={result.reason}>
                                {result.reason || '-'}
                              </TableCell>
                              <TableCell className="text-sm text-muted-foreground">{result.submitted_at}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </motion.div>
          )}

          {activeTab === 'search' && (
            <motion.div
              key="search"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 10 }}
            >
              <SearchSection token={token} isMainUser={isMainUser} onStatsUpdate={refreshStats} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
